import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import { SCALE, USDC, WETH, frxUSD } from "@/domains/tokens/static-tokens"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { approveSuperVoterForAllInteraction } from "@/domains/interactions"
import { superVoteMultipleInteraction } from "@/domains/interactions/super-vote-multiple/super-vote-multiple-interaction"
import { Dates } from "@/domains/library"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

import { SuperVoterView } from "./supervoter-view"

/**
 * Observer component that connects SuperVoter to MobX stores.
 * All state and computation is handled by VotePageStore.
 * This component observes store properties and passes them to the pure View component.
 */
export const SuperVoter = observer(() => {
  const scaleApp = useScaleApplication()
  const account = useAccount()
  const config = useConfig()
  const chainConfig = useChainConfig()
  const emitter = useEventEmitter()

  if (!scaleApp) {
    return null
  }

  const votePageStore = scaleApp.Pages.Vote.Store
  const strategyManagerStore = scaleApp.VoteOptimizer.Store

  // Observe store properties - all computation is in the store
  const totalExpectedIncome = votePageStore.superVoterTotalExpectedIncome
  const poolCount = votePageStore.superVoterPoolCount
  const revenueSources = votePageStore.superVoterRevenueSources
  const totalAllocation = votePageStore.superVoterTotalAllocation
  const maxLockEnabled = votePageStore.maxLockEnabled
  const transactionFailureRisk = votePageStore.transactionFailureRisk
  const hasSuperVoterAccess = votePageStore.hasSuperVoterAccess

  // Get strategy results from StrategyManagerStore
  const strategies = strategyManagerStore.allStrategyResults

  // Get last applied strategy from store
  const lastAppliedStrategy = votePageStore.lastAppliedStrategy

  // Optimization engine selection
  const selectedEngineType = votePageStore.selectedEngineType
  const selectedOptimizationStrategy = votePageStore.selectedOptimizationStrategy
  const selectedTokenStrategy = votePageStore.selectedTokenStrategy

  const selectedTokenForDisplay =
    selectedTokenStrategy != null
      ? scaleApp.Tokens.Repository.find(selectedTokenStrategy) ??
        [SCALE, WETH, USDC, frxUSD].find((t) => t.address.equals(selectedTokenStrategy)) ??
        null
      : null

  const tokenStrategyEstimatedAmount = votePageStore.lastTokenStrategyEstimatedAmount

  // Countdown timer state (updates every second)
  const [countdownText, setCountdownText] = useState(() => {
    const seconds = Dates.secondsUntilNextEpoch()
    return Dates.formatCountdownTimer(seconds)
  })

  useEffect(() => {
    const interval = setInterval(() => {
      const seconds = Dates.secondsUntilNextEpoch()
      setCountdownText(Dates.formatCountdownTimer(seconds))
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const onCastVote = async () => {
    // Validate account
    if (!account.address) {
      emitter.emit("error-super-voting", {
        title: "Vote failed",
        description: "Account not available, do you have a wallet connected?",
        variant: "error",
      })
      return
    }

    // Get transaction data from store
    const voteData = votePageStore.castSuperVoterVote()
    if (!voteData) {
      // Store validation failed - emit appropriate error
      if (!votePageStore.selectedPowerStore || votePageStore.selectedPowerStore.selectedNftIds.length === 0) {
        emitter.emit("error-super-voting", {
          title: "Vote failed",
          description: "Please select at least one NFT to vote with",
          variant: "error",
        })
        return
      }

      const gaugesWithVotes = votePageStore.superVoterRevenueSources.filter(
        (vg: { voteValue: number }) => vg.voteValue > 0,
      )
      if (gaugesWithVotes.length === 0) {
        emitter.emit("error-super-voting", {
          title: "Vote failed",
          description: "Please allocate votes to at least one pool",
          variant: "error",
        })
        return
      }

      const totalAllocation = votePageStore.superVoterTotalAllocation
      if (totalAllocation < 0 || totalAllocation > 100) {
        emitter.emit("error-super-voting", {
          title: "Vote failed",
          description: `Invalid vote allocation: ${totalAllocation.toFixed(1)}%. Allocation must be between 0% and 100%`,
          variant: "error",
        })
        return
      }

      // Generic fallback error
      emitter.emit("error-super-voting", {
        title: "Vote failed",
        description: "Unable to prepare vote transaction. Please check your selections and try again.",
        variant: "error",
      })
      return
    }

    // Execute transaction
    await superVoteMultipleInteraction({
      account: account.address,
      chainId: chainConfig.chainId,
      config,
      superVoterAddress: chainConfig.equalizer.contracts.superVoter,
      emitter,
      eventBus: scaleApp.EventBus,
      ...voteData,
    })
  }

  const onGrantAccess = async () => {
    // Validate account
    if (!account.address) {
      emitter.emit("error-super-voting", {
        title: "Grant access failed",
        description: "Account not available, do you have a wallet connected?",
        variant: "error",
      })
      return
    }

    // Grant access - orchestrator will automatically refresh via domain event
    await approveSuperVoterForAllInteraction({
      account: account.address,
      chainId: chainConfig.chainId,
      config,
      vestedAddress: chainConfig.tokens.vested.address,
      superVoterAddress: chainConfig.equalizer.contracts.superVoter,
      emitter,
      eventBus: scaleApp.EventBus,
    })
  }

  return (
    <SuperVoterView
      totalExpectedIncome={totalExpectedIncome}
      poolCount={poolCount}
      countdownText={countdownText}
      revenueSources={revenueSources}
      totalAllocation={totalAllocation}
      maxLockEnabled={maxLockEnabled}
      transactionFailureRisk={transactionFailureRisk}
      hasSuperVoterAccess={hasSuperVoterAccess}
      selectedEngineType={selectedEngineType}
      selectedOptimizationStrategy={selectedOptimizationStrategy}
      selectedTokenStrategy={selectedTokenStrategy}
      selectedTokenForDisplay={selectedTokenForDisplay}
      tokenStrategyEstimatedAmount={tokenStrategyEstimatedAmount}
      strategies={strategies}
      lastAppliedStrategy={lastAppliedStrategy}
      onRemovePool={(poolId) => votePageStore.removeSuperVoterPool(poolId)}
      onUpdateAllocation={(poolId, allocation) => votePageStore.updateSuperVoterPoolAllocation(poolId, allocation)}
      onReset={() => {
        votePageStore.resetSuperVoterAllocations()
        // Note: lastAppliedStrategy reset is handled by orchestrator on wallet events
      }}
      onMaxLockToggle={(enabled) => votePageStore.setMaxLockEnabled(enabled)}
      onCastVote={onCastVote}
      onGrantAccess={onGrantAccess}
      onApplyPreviousVotes={() => votePageStore.applyPreviousVotes()}
    />
  )
})
